import { Flex, ScrollArea, Text, Box } from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";
import { AnnotatedCartItem } from "./annotated-cart-item";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import { useGondolaChangeGroupsFilters } from "../hooks/useGondolaChangeGroupsFilters";
import { combineCartLinesWithMetrics } from "../lib/helpers";
import { useState, useMemo, useCallback } from "react";

const METRICS = {
  TP: "TP",
  FP: "FP",
  FN: "FN",
} as const;

type MetricType = keyof typeof METRICS;

type CartLine = ReturnType<typeof combineCartLinesWithMetrics>[0];

const filterCartLinesByMetric = (
  cartLines: CartLine[],
  metric: MetricType | null
): CartLine[] => {
  if (!metric) return cartLines;

  const metricFilters: Record<MetricType, (line: CartLine) => boolean> = {
    [METRICS.TP]: (line) => line.tp > 0,
    [METRICS.FP]: (line) => line.fp > 0,
    [METRICS.FN]: (line) => line.fn > 0,
  };

  return cartLines.filter(metricFilters[metric]);
};

const AnnotatedCartSection = () => {
  const { data } = useGondolaChangeGroupsData();
  const { clearSelectedProduct } = useGondolaChangeGroupsFilters();
  const cartLines = useMemo(() => combineCartLinesWithMetrics(data), [data]);
  const [selectedMetric, setSelectedMetric] = useState<MetricType | null>(null);

  const totals = useMemo(
    () =>
      cartLines.reduce(
        (acc, line) => ({
          tp: acc.tp + line.tp,
          fp: acc.fp + line.fp,
          fn: acc.fn + line.fn,
        }),
        { tp: 0, fp: 0, fn: 0 }
      ),
    [cartLines]
  );

  const metrics = useMemo(
    () => [
      { label: METRICS.TP, value: totals.tp },
      { label: METRICS.FP, value: totals.fp },
      { label: METRICS.FN, value: totals.fn },
    ],
    [totals]
  );

  const filteredCartLines = useMemo(
    () => filterCartLinesByMetric(cartLines, selectedMetric),
    [cartLines, selectedMetric]
  );

  const handleMetricClick = useCallback(
    (metricLabel: MetricType) => {
      setSelectedMetric((prev) => (prev === metricLabel ? null : metricLabel));
      clearSelectedProduct();
    },
    [clearSelectedProduct]
  );

  return (
    <Flex flexBasis="1" flexGrow="1" direction="column">
      <Flex direction="row" justify="between" align="center" gap="2" px="4">
        <Text size="3" weight="bold" as="p">
          Annotated Cart ∪ Prediction
        </Text>
        <Flex direction="row" gap="4">
          {metrics.map(({ label, value }) => (
            <Flex
              key={label}
              align="center"
              direction="column"
              gap="2"
              className={twMerge(
                "cursor-pointer p-2 rounded",
                selectedMetric === label
                  ? "bg-blue-100 border border-blue-300"
                  : "hover:bg-gray-50"
              )}
              onClick={() => handleMetricClick(label as MetricType)}
            >
              <Text size="2" weight="bold" color="gray" highContrast>
                {label}
              </Text>
              <Text size="2" weight="bold" color="gray" highContrast>
                {value != null ? value : "-"}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <ScrollArea type="auto" className="h-0 flex-grow">
        <Box p="2">
          {filteredCartLines?.length
            ? filteredCartLines.map((line) => (
                <AnnotatedCartItem
                  key={`${line.sku_base_id}-${line.variant_id}`}
                  name={line.name}
                  imageUrl={line.image_url}
                  barcode={line.barcode}
                  skuId={String(line.sku_base_id)}
                  uom={line.uom}
                  tp={line.tp}
                  fp={line.fp}
                  fn={line.fn}
                />
              ))
            : null}
        </Box>
      </ScrollArea>
    </Flex>
  );
};

export default AnnotatedCartSection;
